import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Badge,
 Box,
 Button,
 Flex,
 IconButton,
 Input,
 Menu,
 MenuButton,
 MenuItem,
 MenuList,
 SimpleGrid,
 Text,
 Tooltip,
 useToast,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff, FiFileText, FiLink, FiMoreVertical, FiEdit2 } from 'react-icons/fi';
import { fetchData } from '../../services/fetch';
import baseService from '../../services/base';
import { Select } from '../../components/Input';
import Loader from '../../components/Feedback/Loader';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import VideoFrame from '../../components/Data/VideoFrame';
import EducationalContentModal from './EducationalContentModal';
import { categoryLabel } from './categories';

export default function EducationalContent() {
 const toast = useToast();
 const [search, setSearch] = useState('');
 const [year, setYear] = useState('');
 const [editing, setEditing] = useState(null);
 const [formOpen, setFormOpen] = useState(false);
 const [togglingId, setTogglingId] = useState(null);

 const { data: contents, isLoading, error, mutate } = useSWR(
  'educational-content',
  fetchData
 );
 const { data: companyGroups } = useSWR('/document/company-groups', fetchData);
 const companyNameById = useMemo(
  () =>
   new Map(
    (companyGroups || []).map((group) => [
     group.id_company_group,
     group.company_name || `Grupo ${group.id_company_group}`,
    ])
   ),
  [companyGroups]
 );

 // Poucos itens (aulas/materiais não têm volume alto) - filtra em memória,
 // igual ao catálogo de indicadores GRI.
 const years = useMemo(() => {
  const unique = new Set((contents || []).map((item) => item.exercise));
  return Array.from(unique).sort((a, b) => b - a);
 }, [contents]);

 const filtered = useMemo(() => {
  const term = search.trim().toLowerCase();
  return (contents || []).filter((item) => {
   if (year && item.exercise !== Number(year)) return false;
   if (!term) return true;
   return (
    item.title?.toLowerCase().includes(term) ||
    item.category?.toLowerCase().includes(term)
   );
  });
 }, [contents, search, year]);

 function openCreate() {
  setEditing(null);
  setFormOpen(true);
 }

 function openEdit(content) {
  setEditing(content);
  setFormOpen(true);
 }

 // Desativar não apaga — só some da tela do cliente, admin continua vendo
 // tudo (ativo e inativo) e pode reativar a qualquer momento.
 async function toggleActive(content) {
  setTogglingId(content.id_educational_content);
  try {
   await baseService.patch(`educational-content/${content.id_educational_content}`, {
    is_active: !(content.is_active !== false),
   });
   await mutate();
  } catch (error) {
   toast({
    title: 'Não foi possível atualizar o conteúdo',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setTogglingId(null);
  }
 }

 return (
  <MainContent>
   <PageHeaderStructure
    title='Aulas e Materiais'
    subtitle='Vídeos do YouTube exibidos pra empresa na área "Aulas e Materiais", organizados por ano.'
   >
    <Button variant='primary' width='230px' height='59px' onClick={openCreate}>
     Novo conteúdo
    </Button>
   </PageHeaderStructure>

   <Flex gap='12px' flexWrap='wrap' align='flex-end' mt='24px' mb='16px'>
    <Box w='320px'>
     <Text fontSize='xs' color='#818A8F' mb='8px'>
      Buscar
     </Text>
     <Input
      placeholder='Título ou categoria'
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      bg='white'
     />
    </Box>

    <Box w='160px'>
     <Select
      name='exercise'
      label='Ano'
      value={year}
      onChange={(e) => setYear(e.target.value)}
     >
      <option value=''>Todos</option>
      {years.map((option) => (
       <option key={option} value={option}>
        {option}
       </option>
      ))}
     </Select>
    </Box>

    <Button
     variant='ghost'
     isDisabled={!search && !year}
     onClick={() => {
      setSearch('');
      setYear('');
     }}
    >
     Limpar filtros
    </Button>

    <Text fontSize='sm' color='#818A8F' ml='auto'>
     {filtered.length} de {contents?.length || 0} conteúdos
    </Text>
   </Flex>

   {error && <Text color='red.600'>Erro ao carregar o conteúdo educativo.</Text>}

   {isLoading ? (
    <Loader />
   ) : (
    <>
     <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing='16px'>
      {filtered.map((content) => (
       <Box
        key={content.id_educational_content}
        bg='white'
        borderRadius='12px'
        overflow='hidden'
        opacity={content.is_active === false ? 0.6 : 1}
       >
        <Box bg='black' aspectRatio='16 / 9' overflow='hidden'>
         <VideoFrame videoUrl={content.youtube_url} width='100%' height='100%' />
        </Box>
        <Box p='16px'>
         <Flex justify='space-between' align='start' gap='8px'>
          <Text fontWeight={600} noOfLines={2}>
           {content.title}
          </Text>
          <Menu placement='bottom-end'>
           <MenuButton
            as={IconButton}
            aria-label='Mais ações'
            icon={<FiMoreVertical />}
            size='sm'
            variant='ghost'
            flexShrink={0}
            isLoading={togglingId === content.id_educational_content}
           />
           <MenuList>
            <MenuItem icon={<FiEdit2 />} onClick={() => openEdit(content)}>
             Editar
            </MenuItem>
            <MenuItem
             icon={content.is_active !== false ? <FiEyeOff /> : <FiEye />}
             onClick={() => toggleActive(content)}
            >
             {content.is_active !== false ? 'Desativar' : 'Ativar'}
            </MenuItem>
           </MenuList>
          </Menu>
         </Flex>

         <Flex align='center' gap='8px' mt='8px' wrap='wrap'>
          <Badge colorScheme='gray' textTransform='none'>
           {content.exercise}
          </Badge>
          {content.is_active === false && (
           <Badge colorScheme='red' textTransform='none'>
            Inativo
           </Badge>
          )}
          {content.category && (
           <Badge colorScheme='blue' textTransform='none'>
            {categoryLabel(content.category)}
           </Badge>
          )}
          {/* Com várias empresas o nome não cabe no card: mostra a contagem e
              a lista no tooltip. Sem empresa marcada = todas (nada a mostrar). */}
          {content.company_groups?.length > 0 && (
           <Tooltip
            hasArrow
            label={content.company_groups
             .map((id) => companyNameById.get(id) || `Grupo ${id}`)
             .join(', ')}
           >
            <Badge colorScheme='purple' textTransform='none' cursor='default'>
             Exclusivo · {content.company_groups.length}{' '}
             {content.company_groups.length > 1 ? 'empresas' : 'empresa'}
            </Badge>
           </Tooltip>
          )}
          {content.material_count > 0 && (
           <Flex align='center' gap='4px' color='#818A8F' fontSize='sm'>
            <FiFileText />
            {content.material_count} {content.material_count > 1 ? 'materiais' : 'material'}
           </Flex>
          )}
          {content.links?.length > 0 && (
           <Flex align='center' gap='4px' color='#818A8F' fontSize='sm'>
            <FiLink />
            {content.links.length} {content.links.length > 1 ? 'links' : 'link'}
           </Flex>
          )}
         </Flex>
        </Box>
       </Box>
      ))}
     </SimpleGrid>

     {filtered.length === 0 && (
      <Text bg='white' borderRadius='12px' px='16px' py='24px' color='#818A8F'>
       {contents?.length
        ? 'Nenhum conteúdo para estes filtros.'
        : 'Nenhum conteúdo cadastrado.'}
      </Text>
     )}
    </>
   )}

   <EducationalContentModal
    isOpen={formOpen}
    onClose={() => setFormOpen(false)}
    content={editing}
    onSaved={mutate}
   />
  </MainContent>
 );
}
